import ShippingAddress from "./ShippingAddress";

export default {
    mapShippingAddressToShippingParty,
    mapShippingPartyToShippingAddress
};

/**
 *
 * @param {ShippingParty} party - Shipping Party from Sales Order Detail
 * @returns {newShop.ShippingAddress}
 */
function mapShippingPartyToShippingAddress(party) {
    const contacts = party.ShippingAddress.Contact;
    const homePhoneContact = contacts ? contacts.find(c => c.Value.Type === "HOMEPHONE") : null;
    const mobilePhoneContact = contacts ? contacts.find(c => c.Value.Type === "MOBILEPHONE") : null;
    const emailContact = contacts ? contacts.find(c => c.Value.Type === "EMAIL") : null;
    const pickupData = party.Custom ? party.Custom.find(c => c.Type === "PICKUPID") : null;

    return new ShippingAddress({
        buyerId: party.ShippingMemberID,
        shippingAddressName: party.ShippingAddress.Name,
        shippingAddress1: party.ShippingAddress.Address.Street1,
        shippingAddress2: party.ShippingAddress.Address.Street2,
        shippingCity: party.ShippingAddress.Address.City,
        shippingCounty: party.ShippingAddress.Address.District,
        shippingState: party.ShippingAddress.Address.Region,
        shippingPostalCode: party.ShippingAddress.Address.PostalCode,
        shippingCountry: party.ShippingAddress.Address.CountryCode,
        shippingPhone: homePhoneContact ? homePhoneContact.Value.value : "",
        shippingMobilePhone: mobilePhoneContact ? mobilePhoneContact.Value.value : "",
        shippingMobilePhoneCountry: mobilePhoneContact ? mobilePhoneContact.Country : "",
        shippingInstructions: party.ShippingAddress.Instructions,
        shippingAlias: party.ShippingAddress.CareOfName,
        addressType: party.Type,
        pickupCenter: pickupData ? pickupData.Value : "",
        shippingEmail: emailContact ? emailContact.Value.value : ""
    });
}

/**
 *
 * @param {newShop.ShippingAddress} address - Shipping Address
 * @returns {ShippingParty}
 */
function mapShippingAddressToShippingParty(address) {

    const Contact = [];
    // Mobile phone is primary.  Only add the home phone if we don't have a mobile phone
    if (address.shippingPhone && !address.shippingMobilePhone) {
        Contact.push({
            Country: address.shippingCountry,
            Value: { value: address.shippingPhone, Type: "HOMEPHONE" }
        });
    }
    if (address.shippingMobilePhone) {
        Contact.push({
            Country: address.shippingMobilePhoneCountry,
            Value: { value: address.shippingMobilePhone, Type: "MOBILEPHONE" }
        });
    }
    if (address.shippingEmail) {
        Contact.push({
            Country: "",
            Value: { value: address.shippingEmail, Type: "EMAIL" }
        });
    }
    const Custom = [];
    if (address.pickupCenter) {
        Custom.push({
            Key: "TYPE",
            Value: address.pickupCenter,
            Type: "PICKUPID"
        });
    }
    return {
        ShippingMemberID: address.buyerId,
        Type: address.addressType,
        ShippingAddress: {
            Name: address.shippingAddressName,
            CareOfName: address.shippingAlias,
            Address: {
                Street1: address.shippingAddress1,
                Street2: address.shippingAddress2,
                City: address.shippingCity,
                District: address.shippingCounty,
                Region: address.shippingState,
                PostalCode: address.shippingPostalCode,
                CountryCode: address.shippingCountry
            },
            Contact,
            Instructions: address.shippingInstructions
        },
        Custom
    }
}

/**
 * Object representing a shipping entity.
 * @typedef ShippingParty
 * @property {string} ShippingMemberID - ID of the user, distributor, or business entity associated with this shipping entity.
 * @property {string} Type - Type of shipping entity.
 * @property {ShippingAddressData} ShippingAddress - Shipping address associated with this entity.
 * @property {Array<CustomField>} Custom - List of custom data fields applied to this shipping entity.
 */

/**
 * Object representing a shipping address.
 * @typedef ShippingAddressData
 * @property {string} [ID] - ID of this shipping address. Applicable only to saved shipping addresses.
 * @property {string} Name - Name of shipping recipient.
 * @property {string} CareOfName - Name of "care of" recipient, if applicable.
 * @property {Address} Address - Physical address.
 * @property {Array<Contact>} Contact - List of contact information entries for this shipping address.
 * @property {string} Instructions - Shipping instructions.
 */

/**
 * Object representing a physical address.
 * @typedef Address
 * @property {string} Street1 - Street address line 1.
 * @property {string} Street2 - Street address line 2.
 * @property {string} City - Name of the city, town, or equivalent.
 * @property {string} District - Name of the district, county, or equivalent.
 * @property {string} [DistrictDesc] - Description of the district, county, or equivalent.
 * @property {string} Region - Name of the region, state, or equivalent.
 * @property {string} [RegionDesc] - Description of the region, state, or equivalent.
 * @property {string} PostalCode - Postal or zip code.
 * @property {string} CountryCode - Country code.
 * @property {string} [CountryCodeDesc] - Description or name of the country.
 * @property {Array<InternationalAddress} [InternationalAddresses] - List of international addresses associated with this address, if applicable.
 */

/**
 *
 * @typedef InternationalAddress
 * @property {string} Type -
 * @property {string} Street1 -
 * @property {string} Street2 -
 * @property {string} City -
 * @property {string} District -
 * @property {string} [DistrictDesc] -
 * @property {string} Region -
 * @property {string} [RegionDesc] -
 * @property {string} PostalCode -
 * @property {string} CountryCode -
 * @property {string} [CountryCodeDesc] -
 */

/**
 * Object representing a shipping recipient's contact information.
 * @typedef Contact
 * @property {string} Country - Country code of the country this contact information applies to.
 * @property {ContactValue} Value - Contact information data.
 */

/**
 * Object representing contact information data.
 * @typedef ContactValue
 * @property {string} value - Contact information value.
 * @property {string} Type - Contact information type: "WORKPHONE", "HOMEPHONE", "MOBILEPHONE", "FAX", "EMAIL".
 */

/**
 * Object representing a custom data field used in communication with Nu Skin Shop APIs.
 * This is used to pass a data value from client to server or vice-versa, where the value
 * in question doesn't fit into the existing API data fields.
 * @typedef CustomField
 * @property {string} Key - Custom field key name.
 * @property {string} Value - Custom field value.
 * @property {string} Type - Custom field type.
 */
